import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, Gift } from 'lucide-react';
import DisclaimerBox from '../components/DisclaimerBox';
import { AddOns } from '../components/AddOns';
import { useCart, FREE_EXPRESS_THRESHOLD } from '../context/CartContext';
import { getFreeGiftOffer } from '../lib/api';

const GiftProgress = ({ subtotal }) => {
  const [offer, setOffer] = useState(null);
  useEffect(() => { getFreeGiftOffer().then((d) => setOffer(d.offer)).catch(() => {}); }, []);
  if (!offer) return null;
  const remaining = offer.minOrder - subtotal;
  const pct = Math.min(100, (subtotal / offer.minOrder) * 100);
  const unlocked = remaining <= 0;
  return (
    <div className={`rounded-lg border px-4 py-3.5 mb-5 ${unlocked ? 'border-alp-gold/60 bg-alp-gold/[0.07]' : 'border-alp-line bg-alp-dark/40'}`} data-testid="gift-progress">
      <p className="flex items-center gap-2 text-xs text-alp-cream/90 leading-relaxed">
        <Gift size={14} className="text-alp-gold shrink-0" />
        {unlocked ? (
          <span data-testid="gift-progress-unlocked"><span className="text-alp-gold font-semibold">Free gift unlocked!</span> Use code <span className="text-alp-gold font-semibold tracking-wider">{offer.code}</span> at checkout for a free {offer.product.name}{offer.product.size ? ` ${offer.product.size}` : ''}.</span>
        ) : (
          <span data-testid="gift-progress-remaining">Spend <span className="text-alp-gold font-semibold">${remaining.toFixed(2)}</span> more to unlock a <span className="text-alp-cream font-medium">free {offer.product.name}</span> with code {offer.code}.</span>
        )}
      </p>
      <div className="mt-2.5 h-1.5 rounded-full bg-alp-line/50 overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-alp-gold/70 to-alp-gold transition-all duration-500" style={{ width: `${pct}%` }} data-testid="gift-progress-bar" />
      </div>
    </div>
  );
};

export default function Cart() {
  const navigate = useNavigate();
  const { items, updateQty, removeFromCart, subtotal, shipping, total, hasPreorder, freeExpress } = useCart();

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-32 text-center">
        <ShoppingBag size={48} strokeWidth={1} className="text-alp-gold mx-auto mb-6" />
        <h1 className="font-serif text-4xl text-alp-cream mb-4">Your Cart is Empty</h1>
        <p className="text-alp-gray mb-8">Explore our research catalog to get started.</p>
        <Link to="/shop" className="inline-flex items-center gap-2 bg-alp-gold text-alp-black px-8 py-4 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors">
          Browse Catalog <ArrowRight size={15} />
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-alp-black min-h-screen aurora-wash relative overflow-hidden">
      <div aria-hidden className="gold-orb w-72 h-72 -right-16 top-40 hidden lg:block" />
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-14">
        <h1 className="font-serif text-5xl text-alp-cream mb-10 heading-glow">Cart</h1>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-4">
            {items.map((i) => (
              <div key={i.id} className="flex gap-4 border border-alp-line rounded p-4 bg-alp-card/40">
                {i.image ? <img src={i.image} alt={i.name} className="w-24 h-24 object-cover rounded bg-alp-dark" /> : <div className="w-24 h-24 rounded bg-alp-dark flex items-center justify-center text-alp-gold font-serif text-2xl">🎁</div>}
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <div>
                      <Link to={`/product/${i.id}`} className="font-serif text-2xl text-alp-cream hover:text-alp-gold transition-colors">{i.name}</Link>
                      <div className="text-alp-gray text-sm">{i.code} &middot; {i.size}</div>
                      <span className={`inline-block mt-1 text-[10px] tracking-[0.15em] uppercase ${i.stockStatus === 'Preorder' ? 'text-alp-gold' : 'text-emerald-300/80'}`}>{i.stockStatus}</span>
                    </div>
                    <button onClick={() => removeFromCart(i.id)} className="text-alp-gray hover:text-red-400 transition-colors p-1"><Trash2 size={18} /></button>
                  </div>
                  <div className="flex justify-between items-center mt-4">
                    <div className="flex items-center border border-alp-line rounded">
                      <button onClick={() => updateQty(i.id, i.qty - 1)} className="p-2 text-alp-cream hover:text-alp-gold"><Minus size={14} /></button>
                      <span className="w-10 text-center text-alp-cream text-sm">{i.qty}</span>
                      <button onClick={() => updateQty(i.id, i.qty + 1)} className="p-2 text-alp-cream hover:text-alp-gold"><Plus size={14} /></button>
                    </div>
                    <div className="text-alp-gold">${(i.price * i.qty).toFixed(2)} <span className="text-alp-gray text-xs">AUD</span></div>
                  </div>
                </div>
              </div>
            ))}
            <AddOns />
          </div>

          <div className="lg:col-span-1">
            <div className="border border-alp-line rounded p-6 bg-alp-card/40 sticky top-28">
              <h2 className="font-serif text-2xl text-alp-cream mb-6">Order Summary</h2>
              <GiftProgress subtotal={subtotal} />
              <div className="space-y-3 text-sm">
                <div className="flex justify-between text-alp-gray"><span>Subtotal</span><span className="text-alp-cream">${subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between text-alp-gray"><span>Express Post</span><span className="text-alp-cream">{shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}</span></div>
                {!freeExpress && (
                  <p className="text-xs text-alp-gray/70">Free Express Post on in-stock orders over ${FREE_EXPRESS_THRESHOLD}.</p>
                )}
                <div className="gold-divider my-4" />
                <div className="flex justify-between text-lg"><span className="text-alp-cream">Total</span><span className="text-alp-gold">${total.toFixed(2)} AUD</span></div>
              </div>

              {hasPreorder && (
                <div className="hairline rounded px-4 py-3 mt-5 text-xs text-alp-cream/80 leading-relaxed">
                  In-stock and preorder items may ship separately. Preorder ETA is usually 14&ndash;21 days after the supplier order is placed.
                </div>
              )}

              <button onClick={() => navigate('/checkout')} className="w-full mt-6 inline-flex items-center justify-center gap-2 bg-alp-gold text-alp-black px-8 py-4 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors">
                Proceed to Checkout <ArrowRight size={15} />
              </button>
              <Link to="/shop" className="block text-center mt-4 text-xs tracking-[0.18em] uppercase text-alp-gray hover:text-alp-gold transition-colors">Continue Shopping</Link>
            </div>
          </div>
        </div>

        <div className="mt-12"><DisclaimerBox /></div>
      </div>
    </div>
  );
}
